import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { merge, Observable, of, Subscription } from 'rxjs';
import { TranslatorComponent } from '../translator.component';

@Component({
  selector: 'atm-translator-toggle',
  templateUrl: './translator-toggle.component.html',
  styleUrls: ['./translator-toggle.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranslatorToggleComponent
  implements AfterContentInit, OnChanges, OnDestroy
{
  private stateChanges = Subscription.EMPTY;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('for')
  translator?: TranslatorComponent;

  @HostBinding('class')
  readonly class = 'atm-translator-toggle';

  @HostBinding('class.atm-translator-toggle-active')
  get isActive(): boolean {
    return Boolean(this.translator && this.translator.isOpened);
  }

  @HostBinding('attr.tabindex')
  tabIndex = 0;

  @Input()
  get disabled(): boolean {
    if (!this.isDisabled && this.translator) {
      return this.translator.disabled;
    }
    return this.isDisabled;
  }
  set disabled(value: boolean) {
    this.isDisabled = coerceBooleanProperty(value);
  }
  private isDisabled = false;

  @Input() countHidden = false;

  @Input('aria-label') ariaLabel = '';

  constructor(
    private cd: ChangeDetectorRef,
    @Attribute('tabindex') defaultTabIndex?: string
  ) {
    this.tabIndex = Number(defaultTabIndex || 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['translator']) {
      this.watchStateChanges();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.unsubscribe();
  }

  ngAfterContentInit(): void {
    this.watchStateChanges();
  }

  open(event: Event): void {
    if (this.translator && !this.disabled) {
      this.translator.open();
      event.stopPropagation();
    }
  }

  private watchStateChanges(): void {
    const translatorStateChanged = this.translator
      ? this.translator.stateChanges
      : (of() as Observable<void>);
    const inputStateChanged =
      this.translator && this.translator.input
        ? this.translator.input.stateChanges
        : (of() as Observable<void>);
    const translatorToggled = this.translator
      ? merge(this.translator.opened, this.translator.closed)
      : (of() as Observable<void>);

    this.stateChanges.unsubscribe();
    this.stateChanges = merge(
      translatorStateChanged,
      inputStateChanged,
      translatorToggled
    ).subscribe(() => this.cd.markForCheck());
  }
}
